import Sentry from "@similarweb/sentry-logger";

import type { IUpdateSharingStateRequest } from "app/components/SharingModal/types";
import { ISharingModalOption } from "app/components/SharingModal/types";
import { DefaultFetchService } from "app/services/fetchService";

let sharedWithMeDashboards: SharedWithMeDashboard[] = [];

const fetchService = DefaultFetchService.getInstance();

export const SHARED_DASHBOARDS_END_POINT = "api/userdata/dashboards/shared";

export interface SharingDashboardSuccessResponse {
    accountIds: number[];
    addedAccountIds: number[];
    addedInvitationIds: number[];
    addedUserIds: number[];
    dashboardTitle: string;
    invitationIds: number[];
    result: boolean;
    userIds: number[];
}

export interface SharingDashboardFailedResponse {
    failedInvites: string[];
    result: boolean;
}

type SharingDashboardResponse = SharingDashboardSuccessResponse | SharingDashboardFailedResponse | boolean;

export interface SharedWithMeDashboard {
    addedTime: string;
    id: string;
    isSubscribedToNotifications: boolean;
    numberOfColumns: number;
    ownerId: number;
    parentId: string;
    parentType: string;
    sharedWithAccounts: string[];
    sharedWithInvitations: string[];
    sharedWithUsers: string[];
    title: string;
    widget: DashboardWidget[];
}

interface DashboardWidget {
    addedTime: string;
    dashboardId: string;
    id: string;
    pos: string;
    properties: string;
}

export const ShareDashboardService = {
    share: ({ dashboardId, userIds, message }: { dashboardId: string; userIds?: number[]; message?: string }) => {
        return new Promise<{
            err?: Error | null;
            success?: boolean;
            response?: SharingDashboardSuccessResponse;
        }>((resolve) => {
            if (!dashboardId) {
                resolve({ err: new Error("dashboardId is undefined") });
            } else {
                const payload = Object.assign(
                    {},
                    {
                        userIds: userIds?.length ? userIds : undefined,
                        message: message !== "" ? message : undefined,
                    },
                );
                fetchService
                    .put<SharingDashboardSuccessResponse>(ShareDashboardService.shareUrl(dashboardId), payload)
                    .then((response) => {
                        resolve({ err: null, success: true, response });
                    })
                    .catch((err) => {
                        resolve({ err });
                    });
            }
        });
    },
    unShare: ({ dashboardId }: { dashboardId: string }) => {
        return new Promise<{ err?: Error | null; success?: boolean; response?: Response }>((resolve) => {
            if (!dashboardId) {
                resolve({ err: new Error("dashboardId is undefined") });
            } else {
                fetchService
                    .delete(ShareDashboardService.unShareUrl(dashboardId))
                    .then(() => {
                        resolve({ err: null, success: true });
                    })
                    .catch((err) => {
                        resolve({ err });
                    });
            }
        });
    },
    getSharedWithMe: (): Promise<{ err?: Error | null; dashboards?: SharedWithMeDashboard[] }> => {
        return new Promise((resolve) => {
            if (sharedWithMeDashboards.length > 0) {
                resolve({ dashboards: sharedWithMeDashboards });
            } else {
                fetchService
                    .get<SharedWithMeDashboard[]>(ShareDashboardService.sharedWithMeUrl())
                    .then((dashboards) => {
                        sharedWithMeDashboards = dashboards;
                        resolve({ err: null, dashboards });
                    })
                    .catch((err) => {
                        resolve({ err });
                    });
            }
        });
    },
    shareUrl: (dashboardId: string) => {
        return `${SHARED_DASHBOARDS_END_POINT}/${dashboardId}`;
    },
    unShareUrl: (dashboardId: string) => {
        return `${SHARED_DASHBOARDS_END_POINT}/${dashboardId}`;
    },
    sharedWithMeUrl: () => {
        return SHARED_DASHBOARDS_END_POINT;
    },
};

export const updateSharingState = ({
    dashboardId,
    newSharingState,
    sharedWithAccounts,
    sharedWithUsers,
    sharedWithInvitations,
    notification,
    notifyUserIds,
    shareEndPoint,
}: IUpdateSharingStateRequest): Promise<SharingDashboardResponse> => {
    try {
        if (
            newSharingState === ISharingModalOption.RESTRICTED &&
            !sharedWithUsers.length &&
            !sharedWithInvitations.length
        ) {
            return fetchService.delete(shareEndPoint);
        }

        const body = {
            accountIds: sharedWithAccounts,
            userIds: sharedWithUsers,
            invitationIds: sharedWithInvitations,
            message: notification,
            notifyUserIds,
        };

        return fetchService.put(shareEndPoint, body);
    } catch (error) {
        Sentry.error(
            `change the sharing state to a dashboard is failed dashboardId:${dashboardId}, sharingState:${newSharingState}`,
            error,
        );
        return Promise.reject(error);
    }
};
